import { ComponentPropsWithoutRef, useRef, useState } from "react";
import React from "react";

interface EllipsisTooltipProps {
  children: React.ReactNode;
  className?: string;
}

const EllipsisTooltip: React.FC<EllipsisTooltipProps> = ({
  children,
  className,
}) => {
  const cellRef = useRef<HTMLDivElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  const handleMouseEnter = () => {
    if (cellRef.current) {
      const isOverflowing =
        cellRef.current.scrollWidth > cellRef.current.clientWidth ||
        cellRef.current.scrollHeight > cellRef.current.clientHeight;
      setShowTooltip(isOverflowing);
    }
  };

  const isNonReactObject = (value: any) =>
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    !React.isValidElement(value);

  // Safely renders children, converting non-React objects to strings.
  // Arrays are walked rather than stringified: a nested array (e.g. `{notes.map(...)}`
  // beside other children) is valid React, and its elements hold circular
  // internal references that make `JSON.stringify` throw.
  const safeChildrenRender = (children: any): any => {
    if (Array.isArray(children)) {
      return children.map((item) => safeChildrenRender(item));
    }

    return isNonReactObject(children) ? JSON.stringify(children) : children;
  };

  const modifiedChildren = React.isValidElement(children)
    ? React.cloneElement(children, {
        ...children.props,
        children: safeChildrenRender(children.props.children),
      })
    : safeChildrenRender(children);

  return (
    <div
      className={className}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setShowTooltip(false)}
      ref={cellRef}
    >
      {modifiedChildren}
      {showTooltip && (
        <div
          className="absolute max-w-40 bg-neutral-light p-2 rounded shadow-md z-10 break-words whitespace-normal"
          style={{
            top: `${
              (cellRef?.current?.getBoundingClientRect().top || 0) -
              (cellRef?.current?.getBoundingClientRect().height || 0)
            }px`,
          }}
        >
          {modifiedChildren}
        </div>
      )}
    </div>
  );
};

export default EllipsisTooltip;
